'use client';

import {
	type Country,
	type CountryVariantTemplate,
	useCountries,
	useIntl,
	useUserCountryQueries
} from '@capsicum/common';
import { useCallback, useEffect, useMemo, useState } from 'react';

import { Resource } from '../../../atoms';
import { BaseSelectModal, type BaseSelectModalProps } from '../select-modal';

import { BaseCountrySelectInputSkeleton } from './BaseCountrySelectSkeleton';
import { BaseCountrySelectTemplate, GetCountryFlag } from './BaseCountrySelectTemplate';
import { CountryCodeWrapper } from './components.styled';

export type CountrySelectModalProps = Omit<
	BaseSelectModalProps<Country>,
	'customLabelProp' | 'data' | 'itemTemplate' | 'onSearch'
> & {
	hiddenCountries?: Array<string>;
	variant?: CountryVariantTemplate;
	withoutInitialValue?: boolean;
};

export const CountrySelectModal = ({
	hiddenCountries = [],
	onChange,
	onClose,
	value,
	variant = 'countryWithFlag',
	withoutInitialValue,
	...props
}: CountrySelectModalProps) => {
	const { countriesQuery } = useCountries();
	const { $t } = useIntl();

	const filteredCountries = useMemo(() => {
		const countries = countriesQuery.data.filter(item => !hiddenCountries?.includes(item.iso2));

		return {
			...countriesQuery,
			data: countries
		};
	}, [countriesQuery, hiddenCountries]);

	const userCountry = useUserCountryQueries();

	const [selectedItem, setSelectedItem] = useState<Country | undefined>(undefined);

	useEffect(() => {
		if (!value) {
			setSelectedItem(undefined);
		}
		if (value) {
			setSelectedItem(value);
		} else if (!withoutInitialValue && !selectedItem) {
			const initialValue = userCountry ?? countriesQuery?.data?.[0];

			setSelectedItem(initialValue);
		}
	}, [value, withoutInitialValue, userCountry, countriesQuery?.data, selectedItem, onChange]);

	const itemTemplate = useCallback(
		({ item }: { item: Country }) => {
			return <BaseCountrySelectTemplate item={item} variant={variant} />;
		},
		[variant]
	);

	const onSearch = useCallback((data: Array<Country>, text: string) => {
		return data.filter(
			({ internationalCallingCode, iso2, name }) =>
				iso2?.toLowerCase().includes(text.toLowerCase()) ||
				internationalCallingCode?.toLowerCase().replace('+', '').startsWith(text.replace('+', '')) ||
				name?.toLowerCase().includes(text.toLowerCase())
		);
	}, []);

	const countryListLabelProp = useCallback(
		(item: Country) => (
			<CountryCodeWrapper>
				<GetCountryFlag iconSize={16} item={item} />
				{variant === 'countryWithCodeAndFlag' ? item.internationalCallingCode : item.name}
			</CountryCodeWrapper>
		),
		[variant]
	);

	const defaultEmptyListMessage: string = useMemo(() => $t('select.searchPlaceholder'), [$t]);

	const filterSearchItems = useCallback((values: Array<Country>, selectedValue?: Country | null) => {
		return selectedValue ? values.filter(({ iso2 }) => iso2 !== selectedValue.iso2) : values;
	}, []);

	return (
		<Resource r={filteredCountries} skeleton={<BaseCountrySelectInputSkeleton />}>
			{data => (
				<BaseSelectModal
					customLabelProp={
						['countryWithCodeAndFlag', 'countryWithFlag'].includes(variant)
							? countryListLabelProp
							: undefined
					}
					data={data}
					emptyListMessage={defaultEmptyListMessage}
					filterSearchItems={filterSearchItems}
					itemTemplate={itemTemplate}
					value={selectedItem}
					onChange={onChange}
					onClose={onClose}
					onSearch={onSearch}
					{...props}
				/>
			)}
		</Resource>
	);
};
